import React, { useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function Aboutus() {
  const timelineRef = useRef(null);
  const lineRef = useRef(null);

  const historyEvents = [
    {
      year: "1906",
      title: "Formation and Leadership",
      points: [
        "All-India Ahl-e-Hadees Conference was established.",
        "Maulana Muhammad Saheb Ghazipuri (Rh) became the first Ameer.",
        "Maulana Sanaullah Amritsari (Rh) served as the first Nazim e Ala."
      ]
    },
    {
      year: "1952–1975",
      title: "Post Partition and Expansion",
      points: [
        "After partition, renamed as Markaz Jamiat Ahle Hadees Hind by Maulana Abdul Wahab Arvi (Rh).",
        "Maulana Abdul Wahab Arvi (Rh) became the first Ameer post-partition.",
        "In 1975, Subai Jamiat Ahle Hadith United Andhra Pradesh formed.",
        "Maulana Abdul Aziz Rahmani Kurnooli (Rh) was appointed Ameer.",
        "Maulana Siraj Ahmed Utkoor became the first Nazim."
      ]
    },
    {
      year: "1975–2015",
      title: "Leadership Progression",
      points: [
        "Maulana Muhammad Muhammadi Saheb served as Ameer.",
        "Followed by Maulana Saeed Ahmed Bastavi and Maulana Khalid Sahib.",
        "Later, Maulana Abdul Rahman Farooqi and Maulana Abdul Basit Riyazi Jamai served.",
        "Maulana Safi Ahmed Madani and Dr. Saeed Ahmed Umari Madani (for one year) also led.",
        "One united Subai Jamiat served both Andhra Pradesh and Telangana for 40 years."
      ]
    },
    {
      year: "2015",
      title: "Formation of Separate States",
      points: [
        "After Telangana formation, two separate Subai Jamiats were created.",
        "Dr. Saeed Ahmed Omari Madani became the first Ameer of Subai Jamiat Ahle Hadith Andhra Pradesh.",
        "Maulana Hafiz Abdul Agni Omari Kurnooli was appointed Nazim.",
        "Mr. Abdul Aziz Khan (Naushad) served as Treasurer."
      ]
    },
    {
      year: "2019",
      title: "Second State Elections",
      points: [
        "Second state election was held on 20 October 2019.",
        "Leadership tradition continued with elected representatives."
      ]
    },
    {
      year: "Present",
      title: "Current Leadership and Reach",
      points: [
        "Maulana Fazlur Rehman Qureshi Omri Hafizahullah is the current Ameer.",
        "Maulana Qazi Syed Munawar Jammai Hafizahullah and Maulana Hafiz Abdul Gaffar Omari Pedna Hafizahullah serve as Nazims.",
        "Mr. Farooq Khan (Vijayawada) is the current Treasurer.",
        "State includes 26 districts, 410 mosques, 108 Seheri Jamiyats, and 410 Maqami Jamiyats."
      ]
    }
  ];

  useEffect(() => {
    if (lineRef.current && timelineRef.current) {
      const cards = timelineRef.current.querySelectorAll('.timeline-card');
      const dots = timelineRef.current.querySelectorAll('.timeline-dot');
      
      gsap.fromTo(
        lineRef.current,
        { height: '0%' },
        {
          height: '100%',
          duration: 2,
          ease: 'none',
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top 60%',
            end: 'bottom 20%',
            scrub: 1,
          }
        }
      );

      cards.forEach((card, index) => {
        gsap.fromTo(
          card,
          {
            opacity: 0,
            x: index % 2 === 0 ? -100 : 100,
            scale: 0.8,
          },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 80%',
              end: 'top 50%',
              toggleActions: 'play none none reverse',
            }
          }
        );

        gsap.fromTo(
          dots[index],
          {
            scale: 0,
            opacity: 0,
          },
          {
            scale: 1,
            opacity: 1,
            duration: 0.5,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: card,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            }
          }
        );

        gsap.to(dots[index], {
          scale: 1.3,
          duration: 1,
          repeat: -1,
          yoyo: true,
          ease: 'power1.inOut',
          scrollTrigger: {
            trigger: card,
            start: 'top 75%',
            toggleActions: 'play pause resume pause',
          }
        });
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative w-full h-screen">
        <img
          src="/images/mosque4.jpg"
          alt="Mosque"
          className="w-full h-full object-cover brightness-75"
        />
        <div className="absolute inset-0 bg-black opacity-35"></div> 
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6 md:px-8">
          <motion.h1 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 drop-shadow-lg leading-tight uppercase"
          >
            History of<br className="sm:hidden"/> Andhra Pradesh<br/>Ahlul Hadeeth
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white pt-4 sm:pt-6 md:pt-0 max-w-4xl text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl drop-shadow-md font-light leading-relaxed px-4"
          >
            For over a century, we have been dedicated to spreading Islamic knowledge, uplifting communities, 
            and preserving our faith. From the establishment of the All-India Ahl-e-Hadees Conference in 1906 
            to our present-day initiatives, our journey reflects our unwavering commitment to service and unity.
          </motion.p>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8 lg:px-12 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-800 mb-3 sm:mb-4 uppercase">
              Who We Are
            </h2>
            <div className="w-20 sm:w-24 h-1 bg-gray-800 mx-auto"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-12 sm:mb-16 max-w-5xl mx-auto"
          >
            <div className="relative bg-white rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed text-center font-light">
                We are an Islamic NGO dedicated to serving humanity in accordance with the teachings of the Quran and Sunnah.
                Our mission focuses on <span className="font-black text-gray-800">education</span>, <span className="font-black text-gray-800">charity</span>, and <span className="font-black text-gray-800">social welfare</span>, aiming to uplift communities and promote justice, compassion, and unity.
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-12 sm:mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gray-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative h-full bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-800 rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-black text-gray-800 mb-4 sm:mb-6 uppercase">
                  Our Mission
                </h3>
                
                <div className="w-12 sm:w-16 h-1 bg-gray-800 mb-4 sm:mb-6"></div>
                
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-light">
                  To serve society through educational initiatives, humanitarian services, and promotion of Islamic values,
                  ensuring dignity and support for all individuals. We are committed to nurturing generations who are 
                  <span className="font-black text-gray-800"> spiritually enriched</span>, 
                  <span className="font-black text-gray-800"> socially responsible</span>, and 
                  <span className="font-black text-gray-800"> globally aware</span>.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gray-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative h-full bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-800 rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-black text-gray-800 mb-4 sm:mb-6 uppercase">
                  Our Vision
                </h3>
                
                <div className="w-12 sm:w-16 h-1 bg-gray-800 mb-4 sm:mb-6"></div>
                
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-light">
                  Building a just, compassionate society rooted in Islamic principles, where every individual has access to opportunity and dignity.
                  We envision a world where 
                  <span className="font-black text-gray-800"> human rights are respected</span>, 
                  <span className="font-black text-gray-800"> diversity is celebrated</span>, and 
                  <span className="font-black text-gray-800"> peace prevails</span> through collective effort and divine guidance.
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6"
          >
            {[
              { number: "100+", label: "Years of Service" },
              { number: "26", label: "Districts" },
              { number: "410", label: "Mosques" },
              { number: "518", label: "Jamiyats" }
            ].map((stat, index) => (
              <div key={index} className="group">
                <div className="relative bg-white rounded-xl p-4 sm:p-6 text-center shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-1 sm:mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 text-xs sm:text-sm md:text-base font-black uppercase">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* History Of Ahle Hadees Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8 lg:px-12 bg-gray-50 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="relative p-6 sm:p-8 md:p-12 lg:p-16">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-3 sm:mb-4 uppercase">
                    History Of Ahle Hadees
                  </h2>
                  <div className="w-20 sm:w-24 h-1 bg-gray-800 mx-auto"></div>
                </div>

                <div className="space-y-6 sm:space-y-8 text-gray-700">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: true }}
                    className="relative bg-gray-50 rounded-2xl p-6 sm:p-8 shadow-md border border-gray-200"
                  >
                    <p className="text-base sm:text-lg md:text-xl leading-relaxed text-center font-light">
                      Islam is a complete code of life based on the Holy Qur'an and the Sunnah of the Prophet Muhammad (SWS).
                      The true spirit of the religion lies in the fact that every Muslim should base their beliefs, acts of worship,
                      dealings, and character solely on the <span className="font-black text-gray-800">Book of Allah</span> and the <span className="font-black text-gray-800">Sunnah of His Messenger</span>.
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-gray-200"
                  >
                    <p className="text-sm sm:text-base md:text-lg leading-relaxed font-light">
                      Throughout Islamic history, whenever differences in religious matters, personal opinions, analogical reasoning,
                      and blind following emerged, there remained a group that upheld the Qur'an and Sunnah as the true foundation of religion.
                      This group came to be known as <span className="inline-block px-3 py-1 bg-gray-800 text-white rounded-lg font-black shadow-sm">Ahl-e-Hadith</span>.
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="relative my-6 sm:my-8"
                  >
                    <div className="relative bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-300 shadow-xl">
                      <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg uppercase">
                        Quranic Guidance
                      </div>
                      
                      <blockquote className="text-center mt-4 sm:mt-6">
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl italic text-gray-700 leading-relaxed mb-3 sm:mb-4 font-light">
                          "Let there arise out of you a group of people who invite to goodness, enjoin what is right,
                          and forbid what is wrong. These are the ones who will be successful."
                        </p>
                        <footer className="text-xs sm:text-sm md:text-base font-black text-gray-800 border-t-2 border-gray-200 pt-3 sm:pt-4 uppercase">
                          — Surah Aal-e-Imran, Ayah 104
                        </footer>
                      </blockquote>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="relative my-6 sm:my-8"
                  >
                    <div className="relative bg-gray-50 rounded-2xl p-6 sm:p-8 border-2 border-gray-300 shadow-xl">
                      <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg uppercase">
                        Prophetic Hadith
                      </div>
                      
                      <blockquote className="text-center mt-4 sm:mt-6">
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl italic text-gray-700 leading-relaxed mb-3 sm:mb-4 font-light">
                          "I am leaving behind two things among you; as long as you hold fast to them, you will never go astray:
                          the Book of Allah and my Sunnah."
                        </p>
                        <footer className="text-xs sm:text-sm md:text-base font-black text-gray-800 border-t-2 border-gray-200 pt-3 sm:pt-4 uppercase">
                          — Muwatta Imam Malik, Hadith 1594
                        </footer>
                      </blockquote>
                    </div>
                  </motion.div>

                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mt-8 sm:mt-12">
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      viewport={{ once: true }}
                      className="relative group"
                    >
                      <div className="relative bg-white rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <h4 className="text-lg sm:text-xl font-black text-gray-800 mb-2 sm:mb-3 uppercase">Early Scholars</h4>
                        <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base font-light">
                          The noble Companions (Sahabah), their followers (Tabi'een), their followers (Taba Tabi'een),
                          and the great scholars of Hadith (Muhadditheen) held firmly to this approach. Scholars like
                          Imam Ahmad ibn Hanbal, Imam Shafi'i, Imam Malik, Imam Bukhari, and Imam Muslim dedicated their lives
                          to preserving the Hadith.
                        </p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                      viewport={{ once: true }}
                      className="relative group"
                    >
                      <div className="relative bg-white rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <h4 className="text-lg sm:text-xl font-black text-gray-800 mb-2 sm:mb-3 uppercase">Noble Mission</h4>
                        <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base font-light">
                          These scholars sought to cleanse the religion from blind following, baseless analogies, personal opinions,
                          and innovations (Bidaa't), giving precedence to the Qur'an, Sunnah, and the understanding of the Companions.
                          Later scholars like Imam Ibn Taymiyyah, Imam Ibn Qayyim, and Muhammad ibn Abdul Wahhab carried forward this mission.
                        </p>
                      </div>
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    viewport={{ once: true }}
                    className="relative mt-8 sm:mt-12"
                  >
                    <div className="relative bg-gray-50 rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl border-2 border-gray-300">
                      <h4 className="text-xl sm:text-2xl md:text-3xl font-black text-center text-gray-800 mb-4 sm:mb-6 uppercase">
                        The Indian Subcontinent
                      </h4>
                      
                      <div className="w-20 sm:w-24 h-1 bg-gray-800 mx-auto mb-6 sm:mb-8"></div>
                      
                      <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 mb-4 sm:mb-6 font-light">
                        In the Indian subcontinent, this methodology was strengthened by <span className="font-black text-gray-800">Shah Waliullah Muhaddith Dehlvi</span>,
                        <span className="font-black text-gray-800"> Syed Ahmad Shaheed</span>, <span className="font-black text-gray-800">Shah Ismail Shaheed</span>, 
                        and <span className="font-black text-gray-800">Syed Nazeer Hussain Dehlvi</span>.
                      </p>
                      
                      <div className="bg-white rounded-xl p-5 sm:p-6 border-l-4 border-gray-800 shadow-md">
                        <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 font-light">
                          In <span className="text-xl sm:text-2xl font-black text-gray-800">1906</span>, this movement gained an organized identity through the establishment of the 
                          <span className="inline-block px-3 py-1 mx-1 sm:mx-2 bg-gray-800 text-white rounded-lg font-black shadow-md">Jamiat Ahl-e-Hadees</span>, 
                          marking a pivotal moment in Islamic history on the subcontinent.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-black text-center text-gray-800 mb-3 sm:mb-4 uppercase"
          >
            Historical Milestones
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center text-gray-600 mb-12 sm:mb-16 text-xs sm:text-sm md:text-base lg:text-lg max-w-2xl mx-auto font-light"
          >
            Journey through our rich history and the milestones that shaped our organization
          </motion.p>

          <div ref={timelineRef} className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 md:transform md:-translate-x-1/2 w-1 bg-gray-200 h-full">
              <div 
                ref={lineRef}
                className="w-full bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 origin-top"
                style={{ height: '0%' }}
              />
            </div>

            <div className="space-y-10 sm:space-y-12 md:space-y-16">
              {historyEvents.map((item, idx) => (
                <div
                  key={idx}
                  className="relative flex items-start"
                >
                  <div 
                    className="timeline-dot absolute left-4 md:left-1/2 transform -translate-x-1/2 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-800 border-3 sm:border-4 border-white shadow-lg z-10"
                  />

                  <div 
                    className={`timeline-card w-full ${
                      idx % 2 === 0 
                        ? 'md:w-[calc(50%-0.5rem)] md:mr-auto md:pr-4 pl-10 sm:pl-12 md:pl-0' 
                        : 'md:w-[calc(50%-0.5rem)] md:ml-auto md:pl-4 pl-10 sm:pl-12'
                    }`}
                  >
                    <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-200">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      <div className="relative p-5 sm:p-6 md:p-8">
                        <div className="inline-block mb-3 sm:mb-4">
                          <span className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-base sm:text-lg md:text-xl font-black shadow-md">
                            {item.year}
                          </span>
                        </div>

                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-gray-800 mb-3 sm:mb-4 group-hover:text-gray-900 transition-colors uppercase">
                          {item.title}
                        </h3>

                        <ul className="space-y-2 sm:space-y-3">
                          {item.points.map((point, idx2) => (
                            <li 
                              key={idx2}
                              className="flex items-start text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed font-light"
                            >
                              <span className="inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-800 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="h-1 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Aboutus;



// import React, { useEffect, useRef } from 'react';
// import { motion } from "framer-motion";
// import gsap from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';

// gsap.registerPlugin(ScrollTrigger);

// function Aboutus() {
//   const timelineRef = useRef(null);
//   const lineRef = useRef(null);

//   const historyEvents = [
//     {
//       year: "1906",
//       title: "Formation and Leadership",
//       points: [
//         "All-India Ahl-e-Hadees Conference was established.",
//         "Maulana Muhammad Saheb Ghazipuri (Rh) became the first Ameer.",
//         "Maulana Sanaullah Amritsari (Rh) served as the first Nazim e Ala."
//       ]
//     },
//     {
//       year: "1952–1975",
//       title: "Post Partition and Expansion",
//       points: [
//         "After partition, renamed as Markaz Jamiat Ahle Hadees Hind by Maulana Abdul Wahab Arvi (Rh).",
//         "Maulana Abdul Wahab Arvi (Rh) became the first Ameer post-partition.",
//         "In 1975, Subai Jamiat Ahle Hadith United Andhra Pradesh formed.",
//         "Maulana Abdul Aziz Rahmani Kurnooli (Rh) was appointed Ameer.",
//         "Maulana Siraj Ahmed Utkoor became the first Nazim."
//       ]
//     },
//     {
//       year: "1975–2015",
//       title: "Leadership Progression",
//       points: [
//         "Maulana Muhammad Muhammadi Saheb served as Ameer.",
//         "Followed by Maulana Saeed Ahmed Bastavi and Maulana Khalid Sahib.",
//         "Later, Maulana Abdul Rahman Farooqi and Maulana Abdul Basit Riyazi Jamai served.",
//         "Maulana Safi Ahmed Madani and Dr. Saeed Ahmed Umari Madani (for one year) also led.",
//         "One united Subai Jamiat served both Andhra Pradesh and Telangana for 40 years."
//       ]
//     },
//     {
//       year: "2015",
//       title: "Formation of Separate States",
//       points: [
//         "After Telangana formation, two separate Subai Jamiats were created.",
//         "Dr. Saeed Ahmed Omari Madani became the first Ameer of Subai Jamiat Ahle Hadith Andhra Pradesh.",
//         "Maulana Hafiz Abdul Agni Omari Kurnooli was appointed Nazim.",
//         "Mr. Abdul Aziz Khan (Naushad) served as Treasurer."
//       ]
//     },
//     {
//       year: "2019",
//       title: "Second State Elections",
//       points: [
//         "Second state election was held on 20 October 2019.",
//         "Leadership tradition continued with elected representatives."
//       ]
//     },
//     {
//       year: "Present",
//       title: "Current Leadership and Reach",
//       points: [
//         "Maulana Fazlur Rehman Qureshi Omri Hafizahullah is the current Ameer.",
//         "Maulana Qazi Syed Munawar Jammai Hafizahullah and Maulana Hafiz Abdul Gaffar Omari Pedna Hafizahullah serve as Nazims.",
//         "Mr. Farooq Khan (Vijayawada) is the current Treasurer.",
//         "State includes 26 districts, 410 mosques, 108 Seheri Jamiyats, and 410 Maqami Jamiyats."
//       ]
//     }
//   ];

//   useEffect(() => {
//     // Animated Line Extension
//     if (lineRef.current && timelineRef.current) {
//       const cards = timelineRef.current.querySelectorAll('.timeline-card');
//       const dots = timelineRef.current.querySelectorAll('.timeline-dot');
      
//       // Animate the vertical line
//       gsap.fromTo(
//         lineRef.current,
//         { height: '0%' },
//         {
//           height: '100%',
//           duration: 2,
//           ease: 'none',
//           scrollTrigger: {
//             trigger: timelineRef.current,
//             start: 'top 60%',
//             end: 'bottom 20%',
//             scrub: 1,
//           }
//         }
//       );

//       // Animate each card and dot
//       cards.forEach((card, index) => {
//         // Card animation
//         gsap.fromTo(
//           card,
//           {
//             opacity: 0,
//             x: index % 2 === 0 ? -100 : 100,
//             scale: 0.8,
//           },
//           {
//             opacity: 1,
//             x: 0,
//             scale: 1,
//             duration: 0.8,
//             ease: 'power3.out',
//             scrollTrigger: {
//               trigger: card,
//               start: 'top 80%',
//               end: 'top 50%',
//               toggleActions: 'play none none reverse',
//             }
//           }
//         );

//         // Dot pulse animation
//         gsap.fromTo(
//           dots[index],
//           {
//             scale: 0,
//             opacity: 0,
//           },
//           {
//             scale: 1,
//             opacity: 1,
//             duration: 0.5,
//             ease: 'back.out(1.7)',
//             scrollTrigger: {
//               trigger: card,
//               start: 'top 75%',
//               toggleActions: 'play none none reverse',
//             }
//           }
//         );

//         // Add continuous pulse to dots
//         gsap.to(dots[index], {
//           scale: 1.3,
//           duration: 1,
//           repeat: -1,
//           yoyo: true,
//           ease: 'power1.inOut',
//           scrollTrigger: {
//             trigger: card,
//             start: 'top 75%',
//             toggleActions: 'play pause resume pause',
//           }
//         });
//       });
//     }

//     return () => {
//       ScrollTrigger.getAll().forEach(trigger => trigger.kill());
//     };
//   }, []);

//   return (
//     <div className="flex flex-col ">
//       {/* Hero Section */}
//       <section className="relative w-full h-screen">
//         <img
//           src="/images/mosque4.jpg"
//           alt="Mosque"
//           className="w-full h-full object-cover brightness-75"
//         />
//         <div className="absolute inset-0 bg-black opacity-35"></div> 
//         <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
//           <motion.h1 
//             initial={{ opacity: 0, y: -50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             className="text-white text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg"
//           >
//             History of Andhra Pradesh Ahlul Hadeeth
//           </motion.h1>
//           <motion.p 
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8, delay: 0.3 }}
//             className="text-white pt-6 md:pt-0 max-w-4xl text-lg md:text-xl drop-shadow-md"
//           >
//             For over a century, we have been dedicated to spreading Islamic knowledge, uplifting communities, 
//             and preserving our faith. From the establishment of the All-India Ahl-e-Hadees Conference in 1906 
//             to our present-day initiatives, our journey reflects our unwavering commitment to service and unity.
//           </motion.p>
//         </div>
//       </section>

//       {/* Who We Are Section - Professional White Design */}
//       <section className="relative py-20 px-6 bg-white overflow-hidden">
//         <div className="max-w-7xl mx-auto relative z-10">
//           {/* Section Header */}
//           <motion.div
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             viewport={{ once: true }}
//             className="text-center mb-16"
//           >
//             <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//               Who We Are
//             </h2>
//             <div className="w-24 h-1 bg-gray-800 mx-auto"></div>
//           </motion.div>

//           {/* Main Description Card */}
//           <motion.div
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6, delay: 0.2 }}
//             viewport={{ once: true }}
//             className="mb-16 max-w-5xl mx-auto"
//           >
//             <div className="relative bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
//               <p className="text-xl text-gray-700 leading-relaxed text-center">
//                 We are an Islamic NGO dedicated to serving humanity in accordance with the teachings of the Quran and Sunnah.
//                 Our mission focuses on <span className="font-semibold text-gray-800">education</span>, <span className="font-semibold text-gray-800">charity</span>, and <span className="font-semibold text-gray-800">social welfare</span>, aiming to uplift communities and promote justice, compassion, and unity.
//               </p>
//             </div>
//           </motion.div>

//           {/* Mission and Vision Cards */}
//           <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
//             {/* Mission Card */}
//             <motion.div
//               initial={{ opacity: 0, x: -50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.6, delay: 0.3 }}
//               viewport={{ once: true }}
//               className="group relative"
//             >
//               <div className="absolute inset-0 bg-gray-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
//               <div className="relative h-full bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
//                 {/* Icon */}
//                 <div className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
//                   <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//                   </svg>
//                 </div>
                
//                 <h3 className="text-3xl font-bold text-gray-800 mb-6">
//                   Our Mission
//                 </h3>
                
//                 <div className="w-16 h-1 bg-gray-800 mb-6"></div>
                
//                 <p className="text-gray-700 leading-relaxed text-lg">
//                   To serve society through educational initiatives, humanitarian services, and promotion of Islamic values,
//                   ensuring dignity and support for all individuals. We are committed to nurturing generations who are 
//                   <span className="font-semibold text-gray-800"> spiritually enriched</span>, 
//                   <span className="font-semibold text-gray-800"> socially responsible</span>, and 
//                   <span className="font-semibold text-gray-800"> globally aware</span>.
//                 </p>
//               </div>
//             </motion.div>

//             {/* Vision Card */}
//             <motion.div
//               initial={{ opacity: 0, x: 50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.6, delay: 0.4 }}
//               viewport={{ once: true }}
//               className="group relative"
//             >
//               <div className="absolute inset-0 bg-gray-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
//               <div className="relative h-full bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
//                 {/* Icon */}
//                 <div className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
//                   <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
//                   </svg>
//                 </div>
                
//                 <h3 className="text-3xl font-bold text-gray-800 mb-6">
//                   Our Vision
//                 </h3>
                
//                 <div className="w-16 h-1 bg-gray-800 mb-6"></div>
                
//                 <p className="text-gray-700 leading-relaxed text-lg">
//                   Building a just, compassionate society rooted in Islamic principles, where every individual has access to opportunity and dignity.
//                   We envision a world where 
//                   <span className="font-semibold text-gray-800"> human rights are respected</span>, 
//                   <span className="font-semibold text-gray-800"> diversity is celebrated</span>, and 
//                   <span className="font-semibold text-gray-800"> peace prevails</span> through collective effort and divine guidance.
//                 </p>
//               </div>
//             </motion.div>
//           </div>

//           {/* Stats Section */}
//           <motion.div
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6, delay: 0.5 }}
//             viewport={{ once: true }}
//             className="grid grid-cols-2 md:grid-cols-4 gap-6"
//           >
//             {[
//               { number: "100+", label: "Years of Service" },
//               { number: "26", label: "Districts" },
//               { number: "410", label: "Mosques" },
//               { number: "518", label: "Jamiyats" }
//             ].map((stat, index) => (
//               <div key={index} className="group">
//                 <div className="relative bg-white rounded-xl p-6 text-center shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
//                   <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">
//                     {stat.number}
//                   </div>
//                   <div className="text-gray-600 text-sm md:text-base font-medium">
//                     {stat.label}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </motion.div>
//         </div>
//       </section>

//       {/* History Of Ahle Hadees Section - Professional White Design */}
//       <section className="relative py-20 px-6 bg-gray-50 overflow-hidden">
//         <div className="max-w-6xl mx-auto relative z-10">
//           <motion.div 
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             viewport={{ once: true }}
//             className="relative"
//           >
//             {/* Main Container */}
//             <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
//               {/* Content */}
//               <div className="relative p-8 md:p-16">
//                 {/* Header */}
//                 <div className="text-center mb-12">
//                   <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                     History Of Ahle Hadees
//                   </h2>
//                   <div className="w-24 h-1 bg-gray-800 mx-auto"></div>
//                 </div>

//                 {/* Content Grid */}
//                 <div className="space-y-8 text-gray-700">
//                   {/* Introduction Paragraph 1 */}
//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.5, delay: 0.1 }}
//                     viewport={{ once: true }}
//                     className="relative bg-gray-50 rounded-2xl p-8 shadow-md border border-gray-200"
//                   >
//                     <p className="text-lg md:text-xl leading-relaxed text-center">
//                       Islam is a complete code of life based on the Holy Qur'an and the Sunnah of the Prophet Muhammad (SWS).
//                       The true spirit of the religion lies in the fact that every Muslim should base their beliefs, acts of worship,
//                       dealings, and character solely on the <span className="font-bold text-gray-800">Book of Allah</span> and the <span className="font-bold text-gray-800">Sunnah of His Messenger</span>.
//                     </p>
//                   </motion.div>

//                   {/* Introduction Paragraph 2 */}
//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.5, delay: 0.2 }}
//                     viewport={{ once: true }}
//                     className="relative bg-white rounded-2xl p-8 shadow-md border border-gray-200"
//                   >
//                     <p className="text-lg leading-relaxed">
//                       Throughout Islamic history, whenever differences in religious matters, personal opinions, analogical reasoning,
//                       and blind following emerged, there remained a group that upheld the Qur'an and Sunnah as the true foundation of religion.
//                       This group came to be known as <span className="inline-block px-3 py-1 bg-gray-800 text-white rounded-lg font-bold shadow-sm">Ahl-e-Hadith</span>.
//                     </p>
//                   </motion.div>

//                   {/* Quranic Verse */}
//                   <motion.div
//                     initial={{ opacity: 0, scale: 0.95 }}
//                     whileInView={{ opacity: 1, scale: 1 }}
//                     transition={{ duration: 0.5, delay: 0.3 }}
//                     viewport={{ once: true }}
//                     className="relative my-8"
//                   >
//                     <div className="relative bg-white rounded-2xl p-8 border-2 border-gray-300 shadow-xl">
//                       <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
//                         Quranic Guidance
//                       </div>
                      
//                       <blockquote className="text-center mt-4">
//                         <p className="text-lg md:text-xl italic text-gray-700 leading-relaxed mb-4">
//                           "Let there arise out of you a group of people who invite to goodness, enjoin what is right,
//                           and forbid what is wrong. These are the ones who will be successful."
//                         </p>
//                         <footer className="text-base font-semibold text-gray-800 border-t-2 border-gray-200 pt-4">
//                           — Surah Aal-e-Imran, Ayah 104
//                         </footer>
//                       </blockquote>
//                     </div>
//                   </motion.div>

//                   {/* Hadith */}
//                   <motion.div
//                     initial={{ opacity: 0, scale: 0.95 }}
//                     whileInView={{ opacity: 1, scale: 1 }}
//                     transition={{ duration: 0.5, delay: 0.4 }}
//                     viewport={{ once: true }}
//                     className="relative my-8"
//                   >
//                     <div className="relative bg-gray-50 rounded-2xl p-8 border-2 border-gray-300 shadow-xl">
//                       <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
//                         Prophetic Hadith
//                       </div>
                      
//                       <blockquote className="text-center mt-4">
//                         <p className="text-lg md:text-xl italic text-gray-700 leading-relaxed mb-4">
//                           "I am leaving behind two things among you; as long as you hold fast to them, you will never go astray:
//                           the Book of Allah and my Sunnah."
//                         </p>
//                         <footer className="text-base font-semibold text-gray-800 border-t-2 border-gray-200 pt-4">
//                           — Muwatta Imam Malik, Hadith 1594
//                         </footer>
//                       </blockquote>
//                     </div>
//                   </motion.div>

//                   {/* Historical Context Cards */}
//                   <div className="grid md:grid-cols-2 gap-6 mt-12">
//                     <motion.div
//                       initial={{ opacity: 0, x: -30 }}
//                       whileInView={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5, delay: 0.5 }}
//                       viewport={{ once: true }}
//                       className="relative group"
//                     >
//                       <div className="relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-1">
//                         <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
//                           <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
//                           </svg>
//                         </div>
//                         <h4 className="text-xl font-bold text-gray-800 mb-3">Early Scholars</h4>
//                         <p className="text-gray-700 leading-relaxed">
//                           The noble Companions (Sahabah), their followers (Tabi'een), their followers (Taba Tabi'een),
//                           and the great scholars of Hadith (Muhadditheen) held firmly to this approach. Scholars like
//                           Imam Ahmad ibn Hanbal, Imam Shafi'i, Imam Malik, Imam Bukhari, and Imam Muslim dedicated their lives
//                           to preserving the Hadith.
//                         </p>
//                       </div>
//                     </motion.div>

//                     <motion.div
//                       initial={{ opacity: 0, x: 30 }}
//                       whileInView={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5, delay: 0.6 }}
//                       viewport={{ once: true }}
//                       className="relative group"
//                     >
//                       <div className="relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 transform hover:-translate-y-1">
//                         <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
//                           <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                         </div>
//                         <h4 className="text-xl font-bold text-gray-800 mb-3">Noble Mission</h4>
//                         <p className="text-gray-700 leading-relaxed">
//                           These scholars sought to cleanse the religion from blind following, baseless analogies, personal opinions,
//                           and innovations (Bidaa't), giving precedence to the Qur'an, Sunnah, and the understanding of the Companions.
//                           Later scholars like Imam Ibn Taymiyyah, Imam Ibn Qayyim, and Muhammad ibn Abdul Wahhab carried forward this mission.
//                         </p>
//                       </div>
//                     </motion.div>
//                   </div>

//                   {/* Indian Subcontinent Section */}
//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.5, delay: 0.7 }}
//                     viewport={{ once: true }}
//                     className="relative mt-12"
//                   >
//                     <div className="relative bg-gray-50 rounded-2xl p-8 md:p-10 shadow-xl border-2 border-gray-300">
//                       <h4 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">
//                         The Indian Subcontinent
//                       </h4>
                      
//                       <div className="w-24 h-1 bg-gray-800 mx-auto mb-8"></div>
                      
//                       <p className="text-lg leading-relaxed text-gray-700 mb-6">
//                         In the Indian subcontinent, this methodology was strengthened by <span className="font-bold text-gray-800">Shah Waliullah Muhaddith Dehlvi</span>,
//                         <span className="font-bold text-gray-800"> Syed Ahmad Shaheed</span>, <span className="font-bold text-gray-800">Shah Ismail Shaheed</span>, 
//                         and <span className="font-bold text-gray-800">Syed Nazeer Hussain Dehlvi</span>.
//                       </p>
                      
//                       <div className="bg-white rounded-xl p-6 border-l-4 border-gray-800 shadow-md">
//                         <p className="text-lg leading-relaxed text-gray-700">
//                           In <span className="text-2xl font-bold text-gray-800">1906</span>, this movement gained an organized identity through the establishment of the 
//                           <span className="inline-block px-3 py-1 mx-2 bg-gray-800 text-white rounded-lg font-bold shadow-md">Jamiat Ahl-e-Hadees</span>, 
//                           marking a pivotal moment in Islamic history on the subcontinent.
//                         </p>
//                       </div>
//                     </div>
//                   </motion.div>
//                 </div>
//               </div>
//             </div>
//           </motion.div>
//         </div>
//       </section>

//       {/* History Timeline - Flag on Pole Style */}
//       <section className="bg-gradient-to-b from-white to-gray-50 py-20 px-4 sm:px-6 md:px-8 overflow-hidden">
//         <div className="max-w-6xl mx-auto">
//           <motion.h2 
//             initial={{ opacity: 0, y: -30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             viewport={{ once: true }}
//             className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-gray-800 mb-4"
//           >
//             Historical Milestones
//           </motion.h2>
          
//           <motion.p
//             initial={{ opacity: 0 }}
//             whileInView={{ opacity: 1 }}
//             transition={{ duration: 0.6, delay: 0.2 }}
//             viewport={{ once: true }}
//             className="text-center text-gray-600 mb-16 text-sm sm:text-base md:text-lg max-w-2xl mx-auto"
//           >
//             Journey through our rich history and the milestones that shaped our organization
//           </motion.p>

//           <div ref={timelineRef} className="relative">
//             {/* Animated Vertical Line (Pole) */}
//             <div className="absolute left-4 md:left-1/2 top-0 md:transform md:-translate-x-1/2 w-1 bg-gray-200 h-full">
//               <div 
//                 ref={lineRef}
//                 className="w-full bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 origin-top"
//                 style={{ height: '0%' }}
//               />
//             </div>

//             {/* Timeline Events */}
//             <div className="space-y-12 md:space-y-16">
//               {historyEvents.map((item, idx) => (
//                 <div
//                   key={idx}
//                   className="relative flex items-start"
//                 >
//                   {/* Timeline Dot - On the line */}
//                   <div 
//                     className="timeline-dot absolute left-4 md:left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-gray-800 border-4 border-white shadow-lg z-10"
//                   />

//                   {/* Timeline Card - Flag style (no gap) */}
//                   <div 
//                     className={`timeline-card w-full ${
//                       idx % 2 === 0 
//                         ? 'md:w-[calc(50%-0.5rem)] md:mr-auto md:pr-4 pl-12 md:pl-0' 
//                         : 'md:w-[calc(50%-0.5rem)] md:ml-auto md:pl-4 pl-12'
//                     }`}
//                   >
//                     <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-200">
//                       {/* Gradient overlay on hover */}
//                       <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
//                       <div className="relative p-6 sm:p-8">
//                         {/* Year Badge */}
//                         <div className="inline-block mb-4">
//                           <span className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-5 py-2 rounded-full text-lg sm:text-xl font-bold shadow-md">
//                             {item.year}
//                           </span>
//                         </div>

//                         {/* Title */}
//                         <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors">
//                           {item.title}
//                         </h3>

//                         {/* Points */}
//                         <ul className="space-y-3">
//                           {item.points.map((point, idx2) => (
//                             <li 
//                               key={idx2}
//                               className="flex items-start text-gray-700 text-sm sm:text-base leading-relaxed"
//                             >
//                               <span className="inline-block w-2 h-2 bg-gray-800 rounded-full mt-2 mr-3 flex-shrink-0" />
//                               <span>{point}</span>
//                             </li>
//                           ))}
//                         </ul>
//                       </div>

//                       {/* Bottom accent line */}
//                       <div className="h-1 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </section>

//     </div>
//   );
// }

// export default Aboutus;




// import React, { useEffect, useRef } from 'react';
// import { motion } from "framer-motion";
// import gsap from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';

// gsap.registerPlugin(ScrollTrigger);

// function Aboutus() {
//   const timelineRef = useRef(null);
//   const lineRef = useRef(null);

//   const historyEvents = [
//     {
//       year: "1906",
//       title: "Formation and Leadership",
//       points: [
//         "All-India Ahl-e-Hadees Conference was established.",
//         "Maulana Muhammad Saheb Ghazipuri (Rh) became the first Ameer.",
//         "Maulana Sanaullah Amritsari (Rh) served as the first Nazim e Ala."
//       ]
//     },
//     {
//       year: "1952–1975",
//       title: "Post Partition and Expansion",
//       points: [
//         "After partition, renamed as Markaz Jamiat Ahle Hadees Hind by Maulana Abdul Wahab Arvi (Rh).",
//         "Maulana Abdul Wahab Arvi (Rh) became the first Ameer post-partition.",
//         "In 1975, Subai Jamiat Ahle Hadith United Andhra Pradesh formed.",
//         "Maulana Abdul Aziz Rahmani Kurnooli (Rh) was appointed Ameer.",
//         "Maulana Siraj Ahmed Utkoor became the first Nazim."
//       ]
//     },
//     {
//       year: "1975–2015",
//       title: "Leadership Progression",
//       points: [
//         "Maulana Muhammad Muhammadi Saheb served as Ameer.",
//         "Followed by Maulana Saeed Ahmed Bastavi and Maulana Khalid Sahib.",
//         "Later, Maulana Abdul Rahman Farooqi and Maulana Abdul Basit Riyazi Jamai served.",
//         "Maulana Safi Ahmed Madani and Dr. Saeed Ahmed Umari Madani (for one year) also led.",
//         "One united Subai Jamiat served both Andhra Pradesh and Telangana for 40 years."
//       ]
//     },
//     {
//       year: "2015",
//       title: "Formation of Separate States",
//       points: [
//         "After Telangana formation, two separate Subai Jamiats were created.",
//         "Dr. Saeed Ahmed Omari Madani became the first Ameer of Subai Jamiat Ahle Hadith Andhra Pradesh.",
//         "Maulana Hafiz Abdul Agni Omari Kurnooli was appointed Nazim.",
//         "Mr. Abdul Aziz Khan (Naushad) served as Treasurer."
//       ]
//     },
//     {
//       year: "2019",
//       title: "Second State Elections",
//       points: [
//         "Second state election was held on 20 October 2019.",
//         "Leadership tradition continued with elected representatives."
//       ]
//     },
//     {
//       year: "Present",
//       title: "Current Leadership and Reach",
//       points: [
//         "Maulana Fazlur Rehman Qureshi Omri Hafizahullah is the current Ameer.",
//         "Maulana Qazi Syed Munawar Jammai Hafizahullah and Maulana Hafiz Abdul Gaffar Omari Pedna Hafizahullah serve as Nazims.",
//         "Mr. Farooq Khan (Vijayawada) is the current Treasurer.",
//         "State includes 26 districts, 410 mosques, 108 Seheri Jamiyats, and 410 Maqami Jamiyats."
//       ]
//     }
//   ];

//   useEffect(() => {
//     // Animated Line Extension
//     if (lineRef.current && timelineRef.current) {
//       const cards = timelineRef.current.querySelectorAll('.timeline-card');
//       const dots = timelineRef.current.querySelectorAll('.timeline-dot');
      
//       // Animate the vertical line
//       gsap.fromTo(
//         lineRef.current,
//         { height: '0%' },
//         {
//           height: '100%',
//           duration: 2,
//           ease: 'none',
//           scrollTrigger: {
//             trigger: timelineRef.current,
//             start: 'top 60%',
//             end: 'bottom 20%',
//             scrub: 1,
//           }
//         }
//       );

//       // Animate each card and dot
//       cards.forEach((card, index) => {
//         // Card animation
//         gsap.fromTo(
//           card,
//           {
//             opacity: 0,
//             x: index % 2 === 0 ? -100 : 100,
//             scale: 0.8,
//           },
//           {
//             opacity: 1,
//             x: 0,
//             scale: 1,
//             duration: 0.8,
//             ease: 'power3.out',
//             scrollTrigger: {
//               trigger: card,
//               start: 'top 80%',
//               end: 'top 50%',
//               toggleActions: 'play none none reverse',
//             }
//           }
//         );

//         // Dot pulse animation
//         gsap.fromTo(
//           dots[index],
//           {
//             scale: 0,
//             opacity: 0,
//           },
//           {
//             scale: 1,
//             opacity: 1,
//             duration: 0.5,
//             ease: 'back.out(1.7)',
//             scrollTrigger: {
//               trigger: card,
//               start: 'top 75%',
//               toggleActions: 'play none none reverse',
//             }
//           }
//         );

//         // Add continuous pulse to dots
//         gsap.to(dots[index], {
//           scale: 1.3,
//           duration: 1,
//           repeat: -1,
//           yoyo: true,
//           ease: 'power1.inOut',
//           scrollTrigger: {
//             trigger: card,
//             start: 'top 75%',
//             toggleActions: 'play pause resume pause',
//           }
//         });
//       });
//     }

//     return () => {
//       ScrollTrigger.getAll().forEach(trigger => trigger.kill());
//     };
//   }, []);

//   return (
//     <div className="flex flex-col ">
//       {/* Hero Section */}
//       <section className="relative w-full h-screen">
//         <img
//           src="/images/mosque4.jpg"
//           alt="Mosque"
//           className="w-full h-full object-cover brightness-75"
//         />
//         <div className="absolute inset-0 bg-black opacity-35"></div> 
//         <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
//           <motion.h1 
//             initial={{ opacity: 0, y: -50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             className="text-white text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg"
//           >
//             History of Andhra Pradesh Ahlul Hadeeth
//           </motion.h1>
//           <motion.p 
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8, delay: 0.3 }}
//             className="text-white pt-6 md:pt-0 max-w-4xl text-lg md:text-xl drop-shadow-md"
//           >
//             For over a century, we have been dedicated to spreading Islamic knowledge, uplifting communities, 
//             and preserving our faith. From the establishment of the All-India Ahl-e-Hadees Conference in 1906 
//             to our present-day initiatives, our journey reflects our unwavering commitment to service and unity.
//           </motion.p>
//         </div>
//       </section>

//       {/* About Section */}
//       <section className="pt-12 px-6">
//         <div className="max-w-3xl mx-auto">
//           <h2 className="text-4xl font-semibold text-center text-gray-800 mb-12">Who We Are</h2>
//           <p className="text-lg text-gray-700 leading-relaxed text-justify mb-8">
//             We are an Islamic NGO dedicated to serving humanity in accordance with the teachings of the Quran and Sunnah.
//             Our mission focuses on education, charity, and social welfare, aiming to uplift communities and promote justice, compassion, and unity.
//           </p>
          
//           <div className="grid md:grid-cols-2 gap-8 pt-12">
//             <motion.div
//               initial={{ opacity: 0, x: -50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.6 }}
//               viewport={{ once: true }}
//             >
//               <h3 className="text-2xl font-semibold text-center text-gray-800 mb-4">Our Mission</h3>
//               <p className="text-gray-600 leading-relaxed text-justify">
//                 To serve society through educational initiatives, humanitarian services, and promotion of Islamic values,
//                 ensuring dignity and support for all individuals. 
//                 We are committed to nurturing generations who are spiritually enriched, socially responsible, and globally aware.
//               </p>
//             </motion.div>
            
//             <motion.div
//               initial={{ opacity: 0, x: 50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.6 }}
//               viewport={{ once: true }}
//             >
//               <h3 className="text-2xl font-semibold text-center text-gray-800 mb-4">Our Vision</h3>
//               <p className="text-gray-600 leading-relaxed text-justify">
//                 Building a just, compassionate society rooted in Islamic principles, where every individual has access to opportunity and dignity.
//                 We envision a world where human rights are respected, diversity is celebrated, and peace prevails through collective effort and divine guidance.
//               </p>
//             </motion.div>
//           </div>
//         </div>

//         {/* History Of Ahle Hadees Section */}
//         <motion.div 
//           initial={{ opacity: 0, y: 50 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           viewport={{ once: true }}
//           className="my-16 md:px-12 lg:px-32 bg-[#fdf7f0] border border-[#e5dcc7] rounded-xl shadow-md"
//         >
//           <div className="p-8 md:p-12">
//             <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-[#3b2f2f] mb-10 border-b-2 pb-2 border-[#c4a484]">
//               History Of Ahle Hadees
//             </h2>
            
//             <div className="space-y-6 text-[#3b2f2f] text-lg leading-relaxed font-serif">
//               <p>
//                 Islam is a complete code of life based on the Holy Qur'an and the Sunnah of the Prophet Muhammad (SWS).
//                 The true spirit of the religion lies in the fact that every Muslim should base their beliefs, acts of worship,
//                 dealings, and character solely on the Book of Allah and the Sunnah of His Messenger.
//               </p>
              
//               <p>
//                 Throughout Islamic history, whenever differences in religious matters, personal opinions, analogical reasoning,
//                 and blind following emerged, there remained a group that upheld the Qur'an and Sunnah as the true foundation of religion.
//                 This group came to be known as <span className="font-bold text-[#7a5c2e] italic">Ahl-e-Hadith</span>.
//               </p>
              
//               <blockquote className="border-l-4 border-[#c4a484] pl-4 italic bg-[#fffaf5] rounded-md p-4">
//                 <p>
//                   "Let there arise out of you a group of people who invite to goodness, enjoin what is right,
//                   and forbid what is wrong. These are the ones who will be successful."
//                 </p>
//                 <footer className="text-sm mt-2 text-right">— Surah Aal-e-Imran, Ayah 104</footer>
//               </blockquote>
              
//               <blockquote className="border-l-4 border-[#c4a484] pl-4 italic bg-[#fffaf5] rounded-md p-4">
//                 <p>
//                   "I am leaving behind two things among you; as long as you hold fast to them, you will never go astray:
//                   the Book of Allah and my Sunnah."
//                 </p>
//                 <footer className="text-sm mt-2 text-right">— Muwatta Imam Malik, Hadith 1594</footer>
//               </blockquote>
              
//               <p>
//                 The noble Companions (Sahabah), their followers (Tabi'een), their followers (Taba Tabi'een),
//                 and the great scholars of Hadith (Muhadditheen) held firmly to this approach. Historically, the term
//                 <span className="font-bold text-[#7a5c2e]"> Ahl-e-Hadith</span> was used for renowned scholars like
//                 Imam Ahmad ibn Hanbal, Imam Shafi'i, Imam Malik, Imam Bukhari, and Imam Muslim—those who dedicated their lives
//                 to preserving the Hadith.
//               </p>
              
//               <p>
//                 These scholars sought to cleanse the religion from blind following, baseless analogies, personal opinions,
//                 and innovations (Bidaa't), giving precedence to the Qur'an, Sunnah, and the understanding of the Companions.
//               </p>
              
//               <p>
//                 Later, scholars such as Imam Ibn Taymiyyah, Imam Ibn Qayyim, and Muhammad ibn Abdul Wahhab (may Allah have mercy on them)
//                 carried forward this noble mission.
//               </p>
              
//               <p>
//                 In the Indian subcontinent, this methodology was strengthened by Shah Waliullah Muhaddith Dehlvi,
//                 Syed Ahmad Shaheed, Shah Ismail Shaheed, and Syed Nazeer Hussain Dehlvi. In 1906, this movement gained
//                 an organized identity through the establishment of the <span className="font-bold text-[#7a5c2e]">Jamiat Ahl-e-Hadees</span>.
//               </p>
//             </div>
//           </div>
//         </motion.div>
//       </section>

//       {/* History Timeline - Flag on Pole Style */}
//       <section className="bg-gradient-to-b from-white to-gray-50 py-20 px-4 sm:px-6 md:px-8 overflow-hidden">
//         <div className="max-w-6xl mx-auto">
//           <motion.h2 
//             initial={{ opacity: 0, y: -30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             viewport={{ once: true }}
//             className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-gray-800 mb-4"
//           >
//             Historical Milestones
//           </motion.h2>
          
//           <motion.p
//             initial={{ opacity: 0 }}
//             whileInView={{ opacity: 1 }}
//             transition={{ duration: 0.6, delay: 0.2 }}
//             viewport={{ once: true }}
//             className="text-center text-gray-600 mb-16 text-sm sm:text-base md:text-lg max-w-2xl mx-auto"
//           >
//             Journey through our rich history and the milestones that shaped our organization
//           </motion.p>

//           <div ref={timelineRef} className="relative">
//             {/* Animated Vertical Line (Pole) */}
//             <div className="absolute left-4 md:left-1/2 top-0 md:transform md:-translate-x-1/2 w-1 bg-gray-200 h-full">
//               <div 
//                 ref={lineRef}
//                 className="w-full bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 origin-top"
//                 style={{ height: '0%' }}
//               />
//             </div>

//             {/* Timeline Events */}
//             <div className="space-y-12 md:space-y-16">
//               {historyEvents.map((item, idx) => (
//                 <div
//                   key={idx}
//                   className="relative flex items-start"
//                 >
//                   {/* Timeline Dot - On the line */}
//                   <div 
//                     className="timeline-dot absolute left-4 md:left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-gray-800 border-4 border-white shadow-lg z-10"
//                   />

//                   {/* Timeline Card - Flag style (no gap) */}
//                   <div 
//                     className={`timeline-card w-full ${
//                       idx % 2 === 0 
//                         ? 'md:w-[calc(50%-0.5rem)] md:mr-auto md:pr-4 pl-12 md:pl-0' 
//                         : 'md:w-[calc(50%-0.5rem)] md:ml-auto md:pl-4 pl-12'
//                     }`}
//                   >
//                     <div className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-200">
//                       {/* Gradient overlay on hover */}
//                       <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
//                       <div className="relative p-6 sm:p-8">
//                         {/* Year Badge */}
//                         <div className="inline-block mb-4">
//                           <span className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-5 py-2 rounded-full text-lg sm:text-xl font-bold shadow-md">
//                             {item.year}
//                           </span>
//                         </div>

//                         {/* Title */}
//                         <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors">
//                           {item.title}
//                         </h3>

//                         {/* Points */}
//                         <ul className="space-y-3">
//                           {item.points.map((point, idx2) => (
//                             <li 
//                               key={idx2}
//                               className="flex items-start text-gray-700 text-sm sm:text-base leading-relaxed"
//                             >
//                               <span className="inline-block w-2 h-2 bg-gray-800 rounded-full mt-2 mr-3 flex-shrink-0" />
//                               <span>{point}</span>
//                             </li>
//                           ))}
//                         </ul>
//                       </div>

//                       {/* Bottom accent line */}
//                       <div className="h-1 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </section>
//     </div>
//   );
// }

// export default Aboutus;






// original design 12/12/25

// import React, { useState } from 'react';
// import { motion } from "framer-motion";

// function Aboutus() {
  
//   const historyEvents = [
//     {
//       year: "1906",
//       title: "Formation and Leadership",
//       points: [
//         "All-India Ahl-e-Hadees Conference was established.",
//         "Maulana Muhammad Saheb Ghazipuri (Rh) became the first Ameer.",
//         "Maulana Sanaullah Amritsari (Rh) served as the first Nazim e Ala."
//       ]
//     },
//     {
//       year: "1952–1975",
//       title: "Post Partition and Expansion",
//       points: [
//         "After partition, renamed as Markaz Jamiat Ahle Hadees Hind by Maulana Abdul Wahab Arvi (Rh).",
//         "Maulana Abdul Wahab Arvi (Rh) became the first Ameer post-partition.",
//         "In 1975, Subai Jamiat Ahle Hadith United Andhra Pradesh formed.",
//         "Maulana Abdul Aziz Rahmani Kurnooli (Rh) was appointed Ameer.",
//         "Maulana Siraj Ahmed Utkoor became the first Nazim."
//       ]
//     },
//     {
//       year: "1975–2015",
//       title: "Leadership Progression",
//       points: [
//         "Maulana Muhammad Muhammadi Saheb served as Ameer.",
//         "Followed by Maulana Saeed Ahmed Bastavi and Maulana Khalid Sahib.",
//         "Later, Maulana Abdul Rahman Farooqi and Maulana Abdul Basit Riyazi Jamai served.",
//         "Maulana Safi Ahmed Madani and Dr. Saeed Ahmed Umari Madani (for one year) also led.",
//         "One united Subai Jamiat served both Andhra Pradesh and Telangana for 40 years."
//       ]
//     },
//     {
//       year: "2015",
//       title: "Formation of Separate States",
//       points: [
//         "After Telangana formation, two separate Subai Jamiats were created.",
//         "Dr. Saeed Ahmed Omari Madani became the first Ameer of Subai Jamiat Ahle Hadith Andhra Pradesh.",
//         "Maulana Hafiz Abdul Agni Omari Kurnooli was appointed Nazim.",
//         "Mr. Abdul Aziz Khan (Naushad) served as Treasurer."
//       ]
//     },
//     {
//       year: "2019",
//       title: "Second State Elections",
//       points: [
//         "Second state election was held on 20 October 2019.",
//         "Leadership tradition continued with elected representatives."
//       ]
//     },
//     {
//       year: "Present",
//       title: "Current Leadership and Reach",
//       points: [
//         "Maulana Fazlur Rehman Qureshi Omri Hafizahullah is the current Ameer.",
//         "Maulana Qazi Syed Munawar Jammai Hafizahullah and Maulana Hafiz Abdul Gaffar Omari Pedna Hafizahullah serve as Nazims.",
//         "Mr. Farooq Khan (Vijayawada) is the current Treasurer.",
//         "State includes 26 districts, 410 mosques, 108 Seheri Jamiyats, and 410 Maqami Jamiyats."
//       ]
//     }
//   ];

  
  
 
//   return (
//     <div className="flex flex-col pb-[600px] md:pb-[320px] lg:pb-[420px]">

//       {/* Hero Section */}
//       <section className="relative w-full h-screen">
//   <img
//     src="/images/mosque4.jpg"
//     alt="Mosque"
//     className="w-full h-full object-cover brightness-75"
//   />
  
//   <div className="absolute inset-0 bg-black opacity-35"></div> 

//   <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
//     <h1 className="text-white text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg">
//       History of Andhra Pradesh Ahlul Hadeeth
//     </h1>
//     <p className="text-white pt-6 md:pt-0 max-w-4xl text-lg md:text-xl drop-shadow-md">
//       For over a century, we have been dedicated to spreading Islamic knowledge, uplifting communities, 
//       and preserving our faith. From the establishment of the All-India Ahl-e-Hadees Conference in 1906 
//       to our present-day initiatives, our journey reflects our unwavering commitment to service and unity.
//     </p>
//   </div>
// </section>


//       {/* About Section */}
//       <section className="pt-12 px-6">
//   <div className="max-w-3xl mx-auto">
//     <h2 className="text-4xl font-semibold text-center text-gray-800 mb-12">Who We Are</h2>
//     <p className="text-lg text-gray-700 leading-relaxed text-justify mb-8">
//       We are an Islamic NGO dedicated to serving humanity in accordance with the teachings of the Quran and Sunnah.
//       Our mission focuses on education, charity, and social welfare, aiming to uplift communities and promote justice, compassion, and unity.
      
//     </p>

//     <div className="grid md:grid-cols-2 gap-8 pt-12">
//       <div>
//         <h3 className="text-2xl font-semibold text-center text-gray-800 mb-4">Our Mission</h3>
//         <p className="text-gray-600 leading-relaxed text-justify">
//           To serve society through educational initiatives, humanitarian services, and promotion of Islamic values,
//           ensuring dignity and support for all individuals. 
//           We are committed to nurturing generations who are spiritually enriched, socially responsible, and globally aware.
//         </p>
//       </div>
//       <div>
//         <h3 className="text-2xl font-semibold text-center text-gray-800 mb-4">Our Vision</h3>
//         <p className="text-gray-600 leading-relaxed text-justify">
//           Building a just, compassionate society rooted in Islamic principles, where every individual has access to opportunity and dignity.
//           We envision a world where human rights are respected, diversity is celebrated, and peace prevails through collective effort and divine guidance.
//         </p>
//       </div>
//     </div>
//   </div>

//   {/* --- "History Of Ahle Hadees" Section --- */}
// <div className="my-16  md:px-12 lg:px-32 bg-[#fdf7f0] border border-[#e5dcc7] rounded-xl shadow-md">
//   <div className="p-8 md:p-12">
//     <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-[#3b2f2f] mb-10 border-b-2 pb-2 border-[#c4a484]">
//       History Of Ahle Hadees
//     </h2>

//     <div className="space-y-6 text-[#3b2f2f] text-lg leading-relaxed font-serif">
//       <p>
//         Islam is a complete code of life based on the Holy Qur’an and the Sunnah of the Prophet Muhammad (SWS).
//         The true spirit of the religion lies in the fact that every Muslim should base their beliefs, acts of worship,
//         dealings, and character solely on the Book of Allah and the Sunnah of His Messenger.
//       </p>

//       <p>
//         Throughout Islamic history, whenever differences in religious matters, personal opinions, analogical reasoning,
//         and blind following emerged, there remained a group that upheld the Qur’an and Sunnah as the true foundation of religion.
//         This group came to be known as <span className="font-bold text-[#7a5c2e] italic">Ahl-e-Hadith</span>.
//       </p>

//       <blockquote className="border-l-4 border-[#c4a484] pl-4 italic bg-[#fffaf5] rounded-md">
//         <p>
//           “Let there arise out of you a group of people who invite to goodness, enjoin what is right,
//           and forbid what is wrong. These are the ones who will be successful.”
//         </p>
//         <footer className="text-sm mt-2 text-right">— Surah Aal-e-Imran, Ayah 104</footer>
//       </blockquote>

//       <blockquote className="border-l-4 border-[#c4a484] pl-4 italic bg-[#fffaf5] rounded-md">
//         <p>
//           “I am leaving behind two things among you; as long as you hold fast to them, you will never go astray:
//           the Book of Allah and my Sunnah.”
//         </p>
//         <footer className="text-sm mt-2 text-right">— Muwatta Imam Malik, Hadith 1594</footer>
//       </blockquote>

//       <p>
//         The noble Companions (Sahabah), their followers (Tabi‘een), their followers (Taba Tabi‘een),
//         and the great scholars of Hadith (Muhadditheen) held firmly to this approach. Historically, the term
//         <span className="font-bold text-[#7a5c2e]"> Ahl-e-Hadith</span> was used for renowned scholars like
//         Imam Ahmad ibn Hanbal, Imam Shafi‘i, Imam Malik, Imam Bukhari, and Imam Muslim—those who dedicated their lives
//         to preserving the Hadith.
//       </p>

//       <p>
//         These scholars sought to cleanse the religion from blind following, baseless analogies, personal opinions,
//         and innovations (Bidaa’t), giving precedence to the Qur’an, Sunnah, and the understanding of the Companions.
//       </p>

//       <p>
//         Later, scholars such as Imam Ibn Taymiyyah, Imam Ibn Qayyim, and Muhammad ibn Abdul Wahhab (may Allah have mercy on them)
//         carried forward this noble mission.
//       </p>

//       <p>
//         In the Indian subcontinent, this methodology was strengthened by Shah Waliullah Muhaddith Dehlvi,
//         Syed Ahmad Shaheed, Shah Ismail Shaheed, and Syed Nazeer Hussain Dehlvi. In 1906, this movement gained
//         an organized identity through the establishment of the <span className="font-bold text-[#7a5c2e]">Jamiat Ahl-e-Hadees</span>.
//       </p>
//     </div>
//   </div>
// </div>

// </section>



//       {/* History Timeline */}
//     <section className="bg-white py-20 px-6 overflow-x-hidden">
//   <div className="max-w-7xl mx-auto">
//     <h2 className="text-4xl font-semibold text-center text-gray-800 mb-16">Historical Milestones</h2>
//     <div className="relative">
//       {/* Vertical Line hidden on mobile */}
//       <div className=" absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-800"></div>

//       {/* Events */}
//       <div className="space-y-16">
//         {historyEvents.map((item, idx) => (
//           <motion.div
//             key={idx}
//             initial={{ opacity: 0, x: idx % 2 === 0 ? -100 : 100 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.5 }}
//             className={`relative flex items-center ${idx % 2 === 0 ? 'justify-start' : 'justify-end'}`}
//           >
//             <div className="w-full md:w-1/2 px-0">
//               <div className="bg-green-50 p-6 rounded-lg shadow">
//                 <h3 className="text-xl font-semibold text-green-800 mb-2">{item.year}</h3>
//                 <h4 className="text-lg font-semibold mb-2">{item.title}</h4>
//                 <ul className="list-disc pl-6 text-green-900 space-y-2">
//   {item.points.map((point, idx2) => (
//     <li key={idx2}>{point}</li>
//   ))}
// </ul>

//               </div>
//             </div>
//           </motion.div>
//         ))}
//       </div>
//     </div>
//   </div>
// </section>
//     </div>
//   );
// }

// export default Aboutus;


